import React, { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { useAuth } from '../../contexts/AuthContext';
import { formatSparks, getLeaderboard, getShop } from '../../api/sparksApi';
import AddyAvatar from './AddyAvatar';

function BoardRow({ row }) {
  const place = row.rank;
  return (
    <li className={`flex items-center gap-3 rounded-2xl border px-3 py-2 bg-white ${row.isYou ? 'border-[#5C3BD1] ring-2 ring-[#E6DDFB]' : 'border-[#E6DDFB]'}`}>
      <span className={`w-10 shrink-0 text-center font-bold text-[#5C3BD1] ${place <= 3 ? 'text-3xl' : 'text-lg'}`}>
        {place <= 3 ? ['🥇', '🥈', '🥉'][place - 1] : place}
      </span>
      <span className="leaderboard-addy"><AddyAvatar equipped={row.equipped} catalogue={row.catalogue} size={104} width={124} animate={false} /></span>
      <span className="flex-1 font-semibold text-[#2F1E6B]">{row.isYou ? `${row.firstName} (you)` : row.firstName}</span>
      <span className="font-bold text-[#2F1E6B]">{formatSparks(row.sparks)} ✨</span>
    </li>
  );
}

/**
 * Weekly Sparks leaderboard (§9.3): top 10 by first name + equipped Addy. Outside the top 10 the child also
 * sees their neighbourhood (±2) with real place numbers, so they know who they can go above.
 */
export default function Leaderboard() {
  const { loggedInChild } = useAuth();
  const childId = loggedInChild?._id;
  const [board, setBoard] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    (async () => {
      try {
        const [b, shop] = await Promise.all([getLeaderboard(childId), getShop(childId).catch(() => null)]);
        if (cancelled) return;
        setBoard(b);
        setCatalogue(shop?.items || []);
        posthog.capture('leaderboard_viewed', {
          ownPosition: b.you?.inTop ? 'top10' : b.you?.rank > 0 ? 'neighbourhood' : 'below',
        });
      } catch {
        if (!cancelled) setError('Could not load the leaderboard right now.');
      }
    })();
    return () => { cancelled = true; };
  }, [childId]);

  if (!childId) return (
    <div className="container mx-auto p-6 text-center">
      <p className="text-[#2F1E6B] font-medium">Please log in as a child first, using the <a href="/children-dashboard" className="underline font-bold">Children Dashboard</a> tab.</p>
    </div>
  );
  if (error) return <div className="container mx-auto p-6 text-red-700">{error}</div>;
  if (!board) return <div className="container mx-auto p-6 text-gray-600">Loading…</div>;
  if (!board.enabled) return <div className="container mx-auto p-6 text-gray-600">The leaderboard is taking a break this week.</div>;

  const neighbourhood = board.neighbourhood || [];

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <h1 className="text-2xl font-bold text-[#2F1E6B] mb-1">Sparks Stars ⭐</h1>
      <p className="text-sm text-gray-600 mb-4">Who has earned the most Sparks this week? Everyone starts again on Monday, and spending your Sparks in the shop never moves you down!</p>

      {board.you.optedOut && (
        <div className="mb-3 text-xs bg-[#F1EEF7] text-[#5C3BD1] rounded-lg px-3 py-2">You are hidden from other children on the leaderboard (a grown-up chose this on the Progress Dashboard). They see you as Someone.</div>
      )}

      <h2 className="text-sm font-semibold text-[#5C3BD1] mb-2">Top 10</h2>
      <ol className="space-y-2">
        {board.top.map((row) => (
          <BoardRow key={`top-${row.rank}-${row.firstName}`} row={{ ...row, catalogue }} />
        ))}
        {!board.top.length && <li className="text-gray-500 text-sm">No Sparks earned yet this week — be the first!</li>}
      </ol>

      {neighbourhood.length > 0 && (
        <div className="mt-6" data-testid="sparks-neighbourhood">
          <h2 className="text-sm font-semibold text-[#5C3BD1] mb-1">Around you</h2>
          <p className="text-xs text-gray-600 mb-2">Beat the Addy just above you to move up!</p>
          <ol className="space-y-2">
            {neighbourhood.map((row) => (
              <BoardRow key={`hood-${row.rank}-${row.firstName}`} row={{ ...row, catalogue }} />
            ))}
          </ol>
        </div>
      )}

      {!board.you.inTop && board.you.rank > 0 && (
        <div className="mt-4 rounded-2xl bg-[#FFF8DC] border border-[#FFD966] px-4 py-3 text-[#2F1E6B]">
          <div className="font-bold">You: number {board.you.rank} · {formatSparks(board.you.sparks)} ✨ this week</div>
          {board.you.gapToNext > 0 && (
            <div className="text-sm">{formatSparks(board.you.gapToNext)} more to overtake the person above you</div>
          )}
          {board.top.length >= 10 && board.you.gapToTop > 0 && (
            <div className="text-sm text-gray-600">{formatSparks(board.you.gapToTop)} more to reach the top 10</div>
          )}
        </div>
      )}

      {!board.you.inTop && board.you.rank === 0 && (
        <div className="mt-4 rounded-2xl bg-[#FFF8DC] border border-[#FFD966] px-4 py-3 text-[#2F1E6B]">
          <div className="font-bold">You: {formatSparks(board.you.sparks)} ✨ this week</div>
          {board.top.length >= 10 && <div className="text-sm">{formatSparks(board.you.gapToTop)} more to reach the top 10</div>}
        </div>
      )}
    </div>
  );
}
